import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import AxionControls from "../../../components/controls/AxionControls";
import {
  deleteWorkOrderTask,
  getWorkOrderTasks,
} from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { getTaskStatusBadge } from "../workOrderEnums";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import WorkOrderTaskFormDialog from "./WorkOrderTaskFormDialog";
import { CmmsListSkeleton, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import { getAssetHierarchyPalette, getContainedButtonSx } from "../../cmms/assetHierarchyStyles";

export default function WorkOrderTaskList({ workOrderId, companyKey, userNameById }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const loadFailed = intl.formatMessage(messages.tasksLoadFailed);
  const [formState, setFormState] = useState({ open: false, task: null });
  const [confirmation, setConfirmation] = useState(null);

  const tasksQuery = useQuery(
    ["cmmsWorkOrderTasks", companyKey, workOrderId],
    () => getWorkOrderTasks(companyKey, workOrderId),
    { enabled: Boolean(companyKey && workOrderId), staleTime: 30000 },
  );

  const deleteMutation = useMutation({
    mutationFn: (taskId) => deleteWorkOrderTask(companyKey, workOrderId, taskId),
  });

  const tasks = (tasksQuery.data ?? []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const handleAdd = () => setFormState({ open: true, task: null });
  const handleEdit = (task) => setFormState({ open: true, task });
  const closeForm = () => setFormState({ open: false, task: null });
  const handleSuccess = async () => {
    await tasksQuery.refetch();
  };

  const handleDelete = (task) => {
    setConfirmation({
      title: intl.formatMessage(messages.tasksDeleteTitle),
      message: intl.formatMessage(messages.tasksDeleteMessage, { title: task.title }),
      confirmText: intl.formatMessage(cmmsMessages.delete),
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(task.id);
          setConfirmation(null);
          pushNotification(intl.formatMessage(messages.toastTaskDeleted));
          await tasksQuery.refetch();
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, loadFailed));
        }
      },
    });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          <FormattedMessage {...messages.tasksCount} values={{ count: tasks.length }} />
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={getContainedButtonSx(cmmsColors)}>
          <FormattedMessage {...messages.tasksAdd} />
        </Button>
      </Stack>

      {tasksQuery.isLoading &&
        <CmmsListSkeleton rows={3} height={28} />
      }
      {tasksQuery.error &&
        <CmmsErrorState error={tasksQuery.error} fallback={loadFailed} onRetry={() => tasksQuery.refetch()} isRetrying={tasksQuery.isFetching} dense />
      }
      {!tasksQuery.isLoading && !tasksQuery.error && tasks.length === 0 &&
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          <FormattedMessage {...messages.tasksEmpty} />
        </Typography>
      }
      {tasks.length > 0 &&
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>#</TableCell>
              <TableCell><FormattedMessage {...messages.fieldTitle} /></TableCell>
              <TableCell><FormattedMessage {...messages.fieldStatus} /></TableCell>
              <TableCell><FormattedMessage {...messages.fieldAssigned} /></TableCell>
              <TableCell sx={{ width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => {
              const badge = getTaskStatusBadge(task.status, intl);
              const assigned = task.assignedResourceId
                ? userNameById?.get(task.assignedResourceId) ?? `#${task.assignedResourceId}`
                : "—";
              return (
                <TableRow key={task.id} hover>
                  <TableCell>{task.sortOrder}</TableCell>
                  <TableCell>{task.title}</TableCell>
                  <TableCell>
                    <Chip label={badge.label} size="small" sx={{ backgroundColor: badge.bg, color: badge.text, fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>{assigned}</TableCell>
                  <TableCell>
                    <IconButton size="small" aria-label={intl.formatMessage(messages.tasksEditTitle)} onClick={() => handleEdit(task)}>
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" aria-label={intl.formatMessage(messages.tasksDeleteTitle)} onClick={() => handleDelete(task)}>
                      <DeleteOutlineIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      }

      <WorkOrderTaskFormDialog
        open={formState.open}
        task={formState.task}
        workOrderId={workOrderId}
        companyKey={companyKey}
        onClose={closeForm}
        onSuccess={handleSuccess}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? "Yes"}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={deleteMutation.isPending || deleteMutation.isLoading} />
    </Box>
  );
}
